import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, setCsrf } from "./api";

export interface WorkspaceSummary {
  id: string;
  name: string;
  organization_id: string;
  organization_name: string;
  timezone: string;
  currency: string;
}

export interface Me {
  kind: "user" | "support" | "platform_user";
  user: { id: string; name: string; email: string; email_verified?: boolean; role?: string };
  csrf_token: string;
  active_workspace_id: string | null;
  active_organization_id: string | null;
  active_role?: string | null;
  active_access_group?: { id: string; name: string | null; is_full_access: boolean } | null;
  workspaces: WorkspaceSummary[];
  permissions: string[];
  is_brand_admin: boolean;
  brand_role?: string | null;
  brand_permissions?: string[];
  is_org_owner: boolean;
  needs_onboarding?: boolean;
  support_grant?: { id: string; expiresAt: string } | null;
  mfa_enrolled?: boolean;
}

interface SessionValue {
  me: Me | null;
  loading: boolean;
  activeWorkspace: WorkspaceSummary | null;
  can: (perm: string) => boolean;
  refresh: () => Promise<unknown>;
  switchWorkspace: (id: string) => Promise<void>;
  logout: () => Promise<void>;
  isPlatform: boolean;
}

const Ctx = React.createContext<SessionValue | null>(null);

export function SessionProvider({ children, platform }: { children: React.ReactNode; platform: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["me", platform],
    queryFn: async () => {
      try {
        const me = await api.get<Me>(platform ? "/api/master/auth/me" : "/api/auth/me");
        setCsrf(me.csrf_token);
        return me;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          setCsrf(null);
          return null;
        }
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
  const me = q.data ?? null;
  const activeWorkspace = me?.workspaces?.find((w) => w.id === me.active_workspace_id) ?? null;
  const value: SessionValue = {
    me,
    loading: q.isLoading,
    activeWorkspace,
    isPlatform: platform,
    can: (perm) => !!me?.permissions?.includes(perm) || !!me?.brand_permissions?.includes(perm),
    refresh: () => q.refetch(),
    async switchWorkspace(id) {
      await api.post("/api/auth/active-workspace", { workspace_id: id });
      // Limpa todo dado do escopo anterior antes de carregar o novo (docs/rules/20-frontend).
      qc.removeQueries({ queryKey: ["ws"] });
      await q.refetch();
    },
    async logout() {
      await api.post("/api/auth/logout").catch(() => undefined);
      setCsrf(null);
      qc.clear();
      location.href = platform ? "/master/login" : "/login";
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = React.useContext(Ctx);
  if (!v) throw new Error("useSession fora do SessionProvider");
  return v;
}

/** Query key com o workspace ativo como prefixo — troca de escopo invalida tudo. */
export function useWsKey(...parts: unknown[]): unknown[] {
  const { me } = useSession();
  return ["ws", me?.active_workspace_id ?? "none", ...parts];
}
